/**
 * rehypeFoldableHeadings — Obsidian-style collapsible headings for the preview.
 *
 * Wraps each h1–h3 heading plus the content that follows it (until the next
 * heading of equal or higher rank) inside a <details open> / <summary> pair:
 *
 *   <details open class="md-fold md-fold-h2">
 *     <summary class="md-fold-summary">
 *       <h2>...</h2>
 *     </summary>
 *     <!-- all sibling nodes until the next h1/h2 -->
 *     <p>...</p>
 *     ...
 *   </details>
 *
 * h4–h6 are left untouched so that fine-grained sub-headings remain static.
 *
 * The plugin operates on the hast (HTML AST) produced by remark-rehype and
 * must be placed AFTER remark-rehype and BEFORE rehype-react in the pipeline.
 *
 * Arrow styling (rotate on open/close) is handled entirely via CSS —
 * no extra DOM element is added here.
 */

import type { Plugin } from 'unified';
import type { Root, Element, RootContent } from 'hast';

/** The heading levels we fold. 1 = h1, 2 = h2, 3 = h3. */
const FOLDABLE_LEVELS = new Set([1, 2, 3]);

/** Parse "h1" → 1, "h2" → 2, etc. Returns 0 for non-heading elements. */
function headingLevel(node: RootContent): number {
  if (node.type !== 'element') return 0;
  const el = node as Element;
  const match = /^h([1-6])$/.exec(el.tagName);
  return match ? parseInt(match[1], 10) : 0;
}

/** Build a hast Element node (typed helper). */
function el(
  tagName: string,
  properties: Element['properties'],
  children: RootContent[],
): Element {
  return { type: 'element', tagName, properties, children };
}

/**
 * Wrap foldable headings in a flat list of hast nodes.
 *
 * Each h1/h2/h3 collects every following sibling whose level is strictly
 * GREATER (i.e. lower rank) than its own level.  That raw slice is then
 * processed recursively so that h2s inside an h1 body, and h3s inside an
 * h2 body, each become their own nested <details> elements.
 *
 * Boundary rule (matches Obsidian):
 *   h1 stops at the next h1              (sibLevel <= 1)
 *   h2 stops at the next h1 or h2        (sibLevel <= 2)
 *   h3 stops at the next h1, h2, or h3   (sibLevel <= 3)
 */
function wrapFoldable(nodes: RootContent[]): RootContent[] {
  const result: RootContent[] = [];
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];
    const level = headingLevel(node);

    if (!FOLDABLE_LEVELS.has(level)) {
      // h4–h6, paragraphs, callouts, etc. — pass through unchanged.
      result.push(node);
      i++;
      continue;
    }

    // Collect raw siblings that belong to this heading's body:
    // stop at the next heading whose level is ≤ this heading's level.
    const rawBody: RootContent[] = [];
    i++;

    while (i < nodes.length) {
      const sibling = nodes[i];
      const sibLevel = headingLevel(sibling);

      if (sibLevel > 0 && sibLevel <= level) break;

      rawBody.push(sibling);
      i++;
    }

    // Recursively wrap any foldable sub-headings inside the collected body.
    const wrappedBody = wrapFoldable(rawBody);

    const summary = el('summary', { className: ['md-fold-summary'] }, [node]);

    const details = el(
      'details',
      {
        open: true,
        className: ['md-fold', `md-fold-h${level}`],
      },
      [summary, ...wrappedBody],
    );

    result.push(details);
  }

  return result;
}

const rehypeFoldableHeadings: Plugin<[], Root> = () => {
  return (tree: Root) => {
    tree.children = wrapFoldable(tree.children as RootContent[]) as Root['children'];
  };
};

export default rehypeFoldableHeadings;
